import { n as MUSE_ERROR_CODES, r as isRecoverableCode, t as MuseError } from "./muse-error-BO65y6cz.js";
import "./errors/index.js";
import { $ as ATHENA_CONTROL_CHARACTERISTIC, A as ATHENA_PRESET_PROFILES, B as imuCountsToPhysical, C as LEGACY_PPG_CHARACTERISTICS, D as DEVICE_CLOCK_HZ_MILLISECOND, E as DEVICE_CLOCK_HZ_COARSE, F as decodeDrlRefCounts, G as ATHENA_SENSOR_SPECS, H as ATHENA_SCALING, I as decodeEegCounts, J as isKnownTag, K as PACKET_HEADER_SIZE, L as decodeImuCounts, M as profileForPreset, N as selectPreset, O as deviceTicksToSeconds, P as decodeBatteryPercent, Q as ATHENA_AUX_CHARACTERISTICS, R as decodeOpticsCounts, S as LEGACY_PPG_CHANNEL_NAMES, T as legacyRole, U as decodeAthenaNotification, V as opticsCountsToUnits, W as decodeAthenaNotificationStrict, X as LsbBitReader, Y as specForTag, Z as MAX_FIELD_WIDTH, _ as LEGACY_CONTROL_CHARACTERISTIC, a as LEGACY_EEG_SAMPLE_RATE, at as isAthenaDataCharacteristic, b as LEGACY_EEG_CHARACTERISTICS, c as LEGACY_IMU_SAMPLES_PER_PACKET, ct as ATHENA_EEG_CHANNELS_8, d as LEGACY_PPG_SAMPLE_RATE, dt as MUSE_COMMANDS, et as ATHENA_DATA_CHARACTERISTICS, f as decodeLegacyEeg, ft as decodeCommand, g as LEGACY_ACCELEROMETER_CHARACTERISTIC, h as decodeLegacyTelemetry, i as LEGACY_EEG_SAMPLES_PER_PACKET, it as isAthenaControlCharacteristic, j as isPresetToken, k as estimateDeviceClock, l as LEGACY_IMU_SAMPLE_RATE, lt as eegChannelLabels, m as decodeLegacyPpg, mt as presetCommand, n as LEGACY_ACCELEROMETER_G_PER_COUNT, nt as MUSE_SERVICE_UUID, o as LEGACY_EEG_ZERO_COUNT, ot as ATHENA_CHANNEL_INFO, p as decodeLegacyImu, pt as encodeCommand, q as SUBPACKET_HEADER_SIZE, r as LEGACY_EEG_MICROVOLTS_PER_COUNT, rt as MUSE_SERVICE_UUID_16, s as LEGACY_GYROSCOPE_DEG_PER_SECOND_PER_COUNT, st as ATHENA_EEG_CHANNELS_4, t as detectProtocol, tt as ATHENA_UNIVERSAL_CHARACTERISTIC, u as LEGACY_PPG_SAMPLES_PER_PACKET, ut as scalpChannelIndices, v as LEGACY_DATA_CHARACTERISTICS, w as LEGACY_TELEMETRY_CHARACTERISTIC, x as LEGACY_GYROSCOPE_CHARACTERISTIC, y as LEGACY_EEG_CHANNEL_NAMES, z as eegCountsToMicrovolts } from "./protocol-Bv-zQkPS.js";
import { n as toCanonicalCharacteristicUuid, t as sameCharacteristicUuid } from "./uuid-D9bCf1aR.js";
import "./transport/index.js";
import { i as systemTimeSource, n as MICROSECONDS_PER_SECOND, r as ManualTimeSource, t as MICROSECONDS_PER_MILLISECOND } from "./timestamp-CRYLj3P9.js";
import { a as WindowedRegressionClock, i as robustClock, n as RobustOffsetClock, o as windowedClock, r as rawClock, s as UNCONVERGED, t as RawArrivalClock } from "./time-B-JTEZC3.js";
//#region src/streams/abort.ts
/**
* Reads an abort flag without letting the compiler narrow it away.
*
* An `AbortSignal` is mutable, but TypeScript's control-flow analysis treats an
* earlier `signal.aborted === false` as holding for the rest of the function —
* so a loop that re-checks the same signal fails to compile, or worse, is
* "optimised" out of a developer's mental model. Routing every check through a
* function call keeps the read honest.
*/
function isAborted(signal) {
	return signal !== void 0 && signal.aborted;
}
//#endregion
//#region src/streams/bounded-stream.ts
/**
* A bounded queue with two ways to consume it.
*
* - {@link subscribe} delivers synchronously as values arrive and never buffers.
*   Right for a fast consumer that must not add latency — a recorder, a meter.
* - {@link chunks} is an async iterable backed by the bounded buffer. Right for
*   a consumer that may fall behind, where the overflow policy decides what
*   gives.
*
* The split matters: a single mechanism would force one consumer's backpressure
* onto the other, and a recorder must never be slowed by a sluggish chart.
*/
var BoundedStream = class {
	#capacity;
	#policy;
	#buffer;
	#listeners = /* @__PURE__ */ new Set();
	#onListenerError;
	#head = 0;
	#size = 0;
	#received = 0;
	#delivered = 0;
	#dropped = 0;
	#highWaterMark = 0;
	#ended = false;
	#failure = null;
	/** Resolves the iterator parked waiting for a value. */
	#wake = null;
	/** Whether a {@link chunks} iterator is currently consuming. */
	#consuming = false;
	constructor(options = {}) {
		const capacity = options.capacity ?? 256;
		if (!Number.isFinite(capacity)) throw new RangeError(`Stream capacity must be a finite number, got ${capacity}`);
		this.#capacity = Math.max(1, Math.floor(capacity));
		this.#policy = options.onOverflow ?? "dropOldest";
		this.#onListenerError = options.onListenerError;
		this.#buffer = Array.from({ length: this.#capacity }, () => void 0);
	}
	get stats() {
		return {
			received: this.#received,
			delivered: this.#delivered,
			dropped: this.#dropped,
			highWaterMark: this.#highWaterMark,
			buffered: this.#size
		};
	}
	get ended() {
		return this.#ended;
	}
	/** Total values lost so far. Never reset; {@link takeDropped} has its own counter. */
	get dropped() {
		return this.#dropped;
	}
	/**
	* Values lost since the previous call, so a chunk can report loss since the
	* last one. Independent of {@link dropped}, which keeps the lifetime total.
	*/
	takeDropped() {
		const value = this.#droppedSinceRead;
		this.#droppedSinceRead = 0;
		return value;
	}
	#droppedSinceRead = 0;
	/**
	* Called by the producer. Never throws: an `"error"` overflow fails the
	* stream rather than the producer, and a throwing listener is reported, not
	* propagated.
	*/
	push(value) {
		if (this.#ended) return;
		this.#received++;
		this.#enqueue(value);
		for (const listener of this.#listeners) try {
			listener(value);
		} catch (error) {
			this.#reportListenerError(error, value);
		}
	}
	#reportListenerError(error, value) {
		const hook = this.#onListenerError;
		if (hook === void 0) {
			queueMicrotask(() => {
				throw error;
			});
			return;
		}
		try {
			hook(error, value);
		} catch (hookError) {
			queueMicrotask(() => {
				throw hookError;
			});
		}
	}
	#enqueue(value) {
		if (this.#size === this.#capacity) switch (this.#policy) {
			case "dropOldest":
				this.#head = (this.#head + 1) % this.#capacity;
				this.#size--;
				this.#dropped++;
				this.#droppedSinceRead++;
				break;
			case "dropNewest":
				this.#dropped++;
				this.#droppedSinceRead++;
				return;
			case "error":
				this.#dropped++;
				this.#droppedSinceRead++;
				this.fail(new MuseError("BUFFER_OVERFLOW", `Stream buffer of ${this.#capacity} overflowed; the consumer is not keeping up`, { details: {
					capacity: this.#capacity,
					received: this.#received
				} }));
				return;
		}
		this.#buffer[(this.#head + this.#size) % this.#capacity] = value;
		this.#size++;
		if (this.#size > this.#highWaterMark) this.#highWaterMark = this.#size;
		this.#wake?.();
	}
	/** Fires for every value, synchronously, without buffering. */
	subscribe(listener) {
		this.#listeners.add(listener);
		return () => {
			this.#listeners.delete(listener);
		};
	}
	/**
	* Async iterable over buffered values.
	*
	* Ends when the stream ends or the signal aborts. If the stream failed, the
	* iterator rejects with that error — a consumer must not silently see a short
	* stream where there was a fault.
	*/
	async *chunks(options = {}) {
		if (this.#consuming) throw new TypeError("BoundedStream already has an active chunks() consumer; use subscribe() for more than one");
		this.#consuming = true;
		try {
			while (true) {
				if (isAborted(options.signal)) return;
				while (this.#size > 0) {
					const value = this.#buffer[this.#head];
					this.#buffer[this.#head] = void 0;
					this.#head = (this.#head + 1) % this.#capacity;
					this.#size--;
					this.#delivered++;
					yield value;
					if (isAborted(options.signal)) return;
				}
				if (this.#failure !== null) throw this.#failure;
				if (this.#ended) return;
				await this.#waitForValue(options.signal);
			}
		} finally {
			this.#consuming = false;
		}
	}
	/** Ends the stream. Buffered values are still delivered. */
	end() {
		if (this.#ended) return;
		this.#ended = true;
		this.#wake?.();
	}
	/** Ends the stream with an error, which any iterator will throw. */
	fail(error) {
		if (this.#ended) return;
		this.#failure = error;
		this.#ended = true;
		this.#wake?.();
	}
	/** Discards buffered values without ending the stream. */
	clear() {
		this.#head = 0;
		this.#size = 0;
		this.#buffer.fill(void 0);
	}
	#waitForValue(signal) {
		return new Promise((resolve) => {
			const done = () => {
				this.#wake = null;
				signal?.removeEventListener("abort", done);
				resolve();
			};
			this.#wake = done;
			signal?.addEventListener("abort", done, { once: true });
		});
	}
};
//#endregion
//#region src/signals/types.ts
const IMU_CHANNELS = [
	"accX",
	"accY",
	"accZ",
	"gyroX",
	"gyroY",
	"gyroZ"
];
/** [M] Accelerometer in g, gyroscope in degrees per second — see `docs/protocol.md`. */
const IMU_CHANNEL_UNITS = {
	accX: "g",
	accY: "g",
	accZ: "g",
	gyroX: "deg/s",
	gyroY: "deg/s",
	gyroZ: "deg/s"
};
/**
* The driven-reference pair, in payload order.
*
* **The pair is measured; which name goes with which position is inferred.**
* Channel 0 sits pinned within a few counts of the converter's mid-scale
* (spread 0.1-0.9 µV across most fixtures) while channel 1 moves far more, and
* channel 1's spread collapses to under 1 µV once the headband is worn — the
* behaviour of a circuit that has a body to drive. Naming follows the order the
* ecosystem writes the pair in; if a hardware source ever settles it the other
* way round, these two labels swap and nothing else changes.
*/
const DRL_REF_CHANNELS = ["DRL", "REF"];
/**
* Optical channel labels for each width.
*
* Positions are left/right and outer/inner, with NIR at 730 nm, IR at 850 nm,
* plus red and ambient in the widest mode. Four-channel mode reports the inner
* pair only.
*/
const OPTICS_CHANNELS_16 = [
	"LO_NIR",
	"RO_NIR",
	"LO_IR",
	"RO_IR",
	"LI_NIR",
	"RI_NIR",
	"LI_IR",
	"RI_IR",
	"LO_RED",
	"RO_RED",
	"LO_AMB",
	"RO_AMB",
	"LI_RED",
	"RI_RED",
	"LI_AMB",
	"RI_AMB"
];
function opticsChannelNames(count) {
	if (count === 16) return OPTICS_CHANNELS_16;
	if (count === 8) return OPTICS_CHANNELS_16.slice(0, 8);
	if (count === 4) return OPTICS_CHANNELS_16.slice(4, 8);
	return Array.from({ length: count }, (_, i) => `OPTICS_${i + 1}`);
}
//#endregion
//#region src/signals/sample-assembler.ts
/**
* How far elapsed time may run ahead of the counter before a wrap is suspected.
*
* The per-sensor counter is 8 bits. An outage of 256 subpackets or more leaves
* it looking almost gapless, so the counter alone can under-count by whole
* multiples of 256. Arrival time cannot say how many were lost — Bluetooth
* buffering makes it too coarse — but it can say that far more time passed than
* the counter admits. Three quarters of a wrap keeps ordinary delivery stalls
* from tripping it.
*/
const WRAP_SUSPICION_SUBPACKETS = 192;
/**
* Sensors whose sample index tracks wall time closely enough to fit a clock to.
*
* Battery arrives every few seconds at a rate that is nominal at best, so its
* `index / rate` timeline bears no useful relation to elapsed time. It is
* stamped with its arrival instead — sub-second precision on a housekeeping
* value would be meaningless anyway.
*/
const CLOCKED_KINDS = /* @__PURE__ */ new Set([
	"eeg",
	"imu",
	"optics",
	"drlref"
]);
var SampleAssembler = class {
	#createClock;
	#maxGap;
	#state = /* @__PURE__ */ new Map();
	/** Set by `reset()`: the timelines that follow are not continuous with the old ones. */
	#afterReset = false;
	constructor(options) {
		this.#createClock = options.createClock;
		this.#maxGap = options.maxPlausibleGap ?? 64;
	}
	/**
	* Forgets every sensor's timeline and clock fit.
	*
	* Call it when the stream is interrupted — a reconnect above all. Keeping the
	* old state splices the new data onto the old timeline as if nothing had
	* happened. After a reset the first chunk of each sensor carries
	* `discontinuity: true`, because the length of the gap is not known.
	*/
	reset() {
		this.#state.clear();
		this.#afterReset = true;
	}
	/**
	* Fit statistics per subpacket tag, for judging timestamp quality.
	*
	* Only sensors that are actually clocked appear. Battery is stamped with its
	* arrival time rather than fitted, so listing an empty fit for it would invite
	* a reader to draw conclusions from a number that means nothing.
	*/
	clockQuality() {
		const out = /* @__PURE__ */ new Map();
		for (const [tag, state] of this.#state) if (state.clock.quality.samples > 0) out.set(tag, state.clock.quality);
		return out;
	}
	/** Chunks produced by one notification, in the order its subpackets appeared. */
	accept(packet, arrivalTime) {
		const out = [];
		for (const subpacket of packet.subpackets) {
			const chunk = this.#assemble(packet, subpacket, arrivalTime);
			if (chunk !== null) out.push(chunk);
		}
		return out;
	}
	#assemble(packet, subpacket, arrivalTime) {
		const spec = subpacket.spec;
		if (spec.kind === "unknown") return null;
		const state = this.#stateFor(subpacket.tag);
		const sequence = subpacket.index ?? packet.sensorSequence;
		this.#accountForLoss(state, sequence, spec.samples, spec.nominalRate, arrivalTime);
		const decoded = this.#decode(subpacket);
		if (decoded === null) return null;
		const sampleCount = decoded.sampleCount;
		const rate = spec.nominalRate > 0 ? spec.nominalRate : 1;
		const clocked = CLOCKED_KINDS.has(spec.kind);
		const timestamps = new Float64Array(sampleCount);
		if (clocked) {
			state.clock.observe(state.sampleIndex / rate, arrivalTime);
			for (let i = 0; i < sampleCount; i++) timestamps[i] = state.clock.map((state.sampleIndex + i) / rate);
		} else for (let i = 0; i < sampleCount; i++) timestamps[i] = arrivalTime;
		const minimumStep = 1;
		for (let i = 0; i < sampleCount; i++) {
			const value = timestamps[i];
			if (Number.isFinite(state.lastTimestamp) && value <= state.lastTimestamp) timestamps[i] = state.lastTimestamp + minimumStep;
			state.lastTimestamp = timestamps[i];
		}
		const quality = state.clock.quality;
		const chunk = {
			signal: decoded.kind,
			channels: decoded.channels,
			channelNames: decoded.channelNames,
			timestamps,
			unit: decoded.unit,
			sampleRate: rate,
			timing: {
				deviceTicks: packet.deviceTicks,
				arrivalTime,
				uncertaintyUs: Number.isFinite(quality.residualStdUs) ? quality.residualStdUs : NaN,
				interpolated: true,
				clockModel: clocked ? state.clock.name : "arrival",
				clockConverged: quality.converged
			},
			sequence,
			dropped: state.droppedSinceChunk,
			sampleIndex: state.sampleIndex,
			...state.discontinuity ? { discontinuity: true } : {},
			...decoded.channelUnits === void 0 ? {} : { channelUnits: decoded.channelUnits }
		};
		state.sampleIndex += sampleCount;
		state.droppedSinceChunk = 0;
		state.discontinuity = false;
		return chunk;
	}
	/**
	* Advances the sample index over data the device sent but the host missed.
	*
	* Without this the timeline would compress: every lost subpacket would pull
	* subsequent samples earlier than they really were, and the error would
	* accumulate for the rest of the session.
	*/
	#accountForLoss(state, sequence, samplesPerSubpacket, nominalRate, arrivalTime) {
		if (state.lastSequence !== null) {
			const gap = (sequence - state.lastSequence + 256) % 256;
			const elapsedSeconds = Number.isFinite(state.lastArrival) ? (arrivalTime - state.lastArrival) / 1e6 : 0;
			if (gap > this.#maxGap || nominalRate > 0 && elapsedSeconds * (nominalRate / samplesPerSubpacket) - gap > WRAP_SUSPICION_SUBPACKETS) {
				state.discontinuity = true;
				state.sampleIndex += Math.max(0, Math.round(elapsedSeconds * nominalRate) - samplesPerSubpacket);
			} else if (gap > 1) {
				const lostSamples = (gap - 1) * samplesPerSubpacket;
				state.sampleIndex += lostSamples;
				state.droppedSinceChunk += lostSamples;
			}
		}
		state.lastSequence = sequence;
		state.lastArrival = arrivalTime;
	}
	#stateFor(tag) {
		let state = this.#state.get(tag);
		if (state === void 0) {
			state = {
				sampleIndex: 0,
				lastSequence: null,
				droppedSinceChunk: 0,
				clock: this.#createClock(),
				lastTimestamp: NaN,
				lastArrival: NaN,
				discontinuity: this.#afterReset
			};
			this.#state.set(tag, state);
		}
		return state;
	}
	#decode(subpacket) {
		switch (subpacket.spec.kind) {
			case "eeg": {
				const block = decodeEegCounts(subpacket);
				const values = eegCountsToMicrovolts(block.counts);
				const names = eegChannelLabels(block.channels);
				return {
					kind: "eeg",
					unit: "uV",
					channels: deinterleave(values, block.channels, block.samples, names),
					channelNames: names,
					sampleCount: block.samples
				};
			}
			case "imu": {
				const block = decodeImuCounts(subpacket);
				const values = imuCountsToPhysical(block);
				return {
					kind: "imu",
					unit: "g",
					channelUnits: IMU_CHANNEL_UNITS,
					channels: deinterleave(values, block.channels, block.samples, IMU_CHANNELS),
					channelNames: IMU_CHANNELS,
					sampleCount: block.samples
				};
			}
			case "optics": {
				const block = decodeOpticsCounts(subpacket);
				const values = opticsCountsToUnits(block.counts);
				const names = opticsChannelNames(block.channels);
				return {
					kind: "optics",
					unit: "au",
					channels: deinterleave(values, block.channels, block.samples, names),
					channelNames: names,
					sampleCount: block.samples
				};
			}
			case "drlref": {
				const block = decodeDrlRefCounts(subpacket);
				return {
					kind: "drlref",
					unit: "uV",
					channels: deinterleave(eegCountsToMicrovolts(block.counts), block.channels, block.samples, DRL_REF_CHANNELS),
					channelNames: DRL_REF_CHANNELS,
					sampleCount: block.samples
				};
			}
			case "battery": {
				const percent = decodeBatteryPercent(subpacket);
				return {
					kind: "battery",
					unit: "percent",
					channels: { percent: Float32Array.of(percent) },
					channelNames: ["percent"],
					sampleCount: 1
				};
			}
			default: return null;
		}
	}
};
/** Splits sample-major interleaved values into one array per channel. */
function deinterleave(values, channels, samples, names) {
	const out = {};
	for (let c = 0; c < channels; c++) {
		const column = new Float32Array(samples);
		for (let s = 0; s < samples; s++) column[s] = values[s * channels + c];
		out[names[c] ?? `ch${c}`] = column;
	}
	return out;
}
//#endregion
//#region src/signals/legacy-assembler.ts
const SLOTS = /* @__PURE__ */ new Map();
for (const [index, uuid] of LEGACY_EEG_CHARACTERISTICS.entries()) SLOTS.set(toCanonicalCharacteristicUuid(uuid), {
	signal: "eeg",
	channel: LEGACY_EEG_CHANNEL_NAMES[index],
	index
});
for (const [index, uuid] of LEGACY_PPG_CHARACTERISTICS.entries()) SLOTS.set(toCanonicalCharacteristicUuid(uuid), {
	signal: "ppg",
	channel: LEGACY_PPG_CHANNEL_NAMES[index],
	index
});
const ACCELEROMETER = toCanonicalCharacteristicUuid(LEGACY_ACCELEROMETER_CHARACTERISTIC);
const GYROSCOPE = toCanonicalCharacteristicUuid(LEGACY_GYROSCOPE_CHARACTERISTIC);
const TELEMETRY = toCanonicalCharacteristicUuid(LEGACY_TELEMETRY_CHARACTERISTIC);
const RATE = {
	eeg: 256,
	ppg: 64,
	imu: 52
};
const SAMPLES = {
	eeg: 12,
	ppg: 6,
	imu: 3
};
const UNIT = {
	eeg: "uV",
	ppg: "au",
	imu: "g"
};
/**
* Assembles pre-Athena notifications into the same `SignalChunk` the Athena
* codec produces.
*
* The channel set is **learned from the data**, because a preset command is
* never acknowledged and so the only reliable account of what a device is
* sending is what it sends. The consequence is a short warm-up: the first
* notification completes a one-channel chunk, and chunks widen over the next
* packet or two until every characteristic has reported in. Steady state
* arrives after one packet per channel — well under a tenth of a second.
*
* **Untested against hardware.** The decoders beneath it are checked against
* vectors derived from a working reference implementation, and this alignment
* layer is checked against synthetic notifications — but no Muse 2 or Muse S
* has ever streamed into it. Treat every one of these devices as experimental
* until someone contributes a recording.
*/
var LegacyAssembler = class {
	#createClock;
	#maxPending;
	#state = /* @__PURE__ */ new Map();
	constructor(options) {
		this.#createClock = options.createClock;
		this.#maxPending = options.maxPendingGroups ?? 4;
	}
	/** Decodes one notification, returning any chunks it completed. */
	accept(characteristic, data, arrivalTime) {
		const uuid = toCanonicalCharacteristicUuid(characteristic);
		if (uuid === TELEMETRY) {
			const telemetry = decodeLegacyTelemetry(data);
			return [this.#single("telemetry", telemetry.sequence, {
				percent: Float32Array.of(telemetry.batteryPercent),
				fuelGaugeMillivolts: Float32Array.of(telemetry.fuelGaugeMillivolts),
				temperatureCelsius: Float32Array.of(telemetry.temperatureCelsius)
			}, [
				"percent",
				"fuelGaugeMillivolts",
				"temperatureCelsius"
			], "percent", arrivalTime)];
		}
		if (uuid === ACCELEROMETER || uuid === GYROSCOPE) {
			const accelerometer = uuid === ACCELEROMETER;
			const decoded = decodeLegacyImu(data, accelerometer ? LEGACY_ACCELEROMETER_G_PER_COUNT : LEGACY_GYROSCOPE_DEG_PER_SECOND_PER_COUNT);
			const names = accelerometer ? [
				"accX",
				"accY",
				"accZ"
			] : [
				"gyroX",
				"gyroY",
				"gyroZ"
			];
			const columns = /* @__PURE__ */ new Map();
			for (const [axis, name] of names.entries()) {
				const column = /* @__PURE__ */ new Float32Array(3);
				for (let s = 0; s < 3; s++) column[s] = decoded.values[s * 3 + axis];
				columns.set(name, column);
			}
			return this.#contribute("imu", decoded.sequence, columns, arrivalTime);
		}
		const slot = SLOTS.get(uuid);
		if (slot === void 0) return [];
		const decoded = slot.signal === "eeg" ? decodeLegacyEeg(data) : decodeLegacyPpg(data);
		return this.#contribute(slot.signal, decoded.sequence, /* @__PURE__ */ new Map([[slot.channel, decoded.values]]), arrivalTime);
	}
	/** Flushes every partially-filled group. Call when the stream ends. */
	flush() {
		const out = [];
		for (const [signal, state] of this.#state) for (const sequence of [...state.pending.keys()].toSorted((a, b) => a - b)) {
			const group = state.pending.get(sequence);
			if (group === void 0) continue;
			state.pending.delete(sequence);
			out.push(this.#emit(signal, state, group));
		}
		return out;
	}
	#stateFor(signal) {
		let state = this.#state.get(signal);
		if (state === void 0) {
			state = {
				clock: this.#createClock(),
				pending: /* @__PURE__ */ new Map(),
				seen: /* @__PURE__ */ new Set(),
				newest: -1,
				sampleIndex: 0,
				lastSequence: null
			};
			this.#state.set(signal, state);
		}
		return state;
	}
	#contribute(signal, sequence, columns, arrivalTime) {
		const state = this.#stateFor(signal);
		for (const name of columns.keys()) state.seen.add(name);
		let group = state.pending.get(sequence);
		if (group === void 0) {
			group = {
				sequence,
				channels: /* @__PURE__ */ new Map(),
				arrivalTime
			};
			state.pending.set(sequence, group);
		}
		for (const [name, values] of columns) group.channels.set(name, values);
		if (arrivalTime < group.arrivalTime) group.arrivalTime = arrivalTime;
		if (sequence > state.newest) state.newest = sequence;
		const out = [];
		if (group.channels.size >= state.seen.size) {
			state.pending.delete(sequence);
			out.push(this.#emit(signal, state, group));
		}
		for (const [pendingSequence, pending] of [...state.pending].toSorted((a, b) => a[0] - b[0])) {
			if (state.newest - pendingSequence < this.#maxPending) break;
			state.pending.delete(pendingSequence);
			out.push(this.#emit(signal, state, pending));
		}
		return out;
	}
	#emit(signal, state, group) {
		const rate = RATE[signal] ?? 1;
		const sampleCount = SAMPLES[signal] ?? 1;
		const names = [...group.channels.keys()].toSorted();
		state.clock.observe(state.sampleIndex / rate, group.arrivalTime);
		const timestamps = new Float64Array(sampleCount);
		for (let i = 0; i < sampleCount; i++) timestamps[i] = state.clock.map((state.sampleIndex + i) / rate);
		const previous = state.lastSequence;
		const step = previous === null ? 1 : (group.sequence - previous + 65536) % 65536;
		const dropped = previous === null || step <= 1 ? 0 : (step - 1) * sampleCount;
		state.lastSequence = group.sequence;
		const channels = {};
		for (const name of names) channels[name] = group.channels.get(name);
		const chunk = {
			signal,
			channels,
			channelNames: names,
			timestamps,
			unit: UNIT[signal] ?? "au",
			sampleRate: rate,
			timing: {
				deviceTicks: group.sequence,
				arrivalTime: group.arrivalTime,
				uncertaintyUs: state.clock.quality.residualStdUs,
				interpolated: true,
				clockModel: state.clock.name,
				clockConverged: state.clock.quality.converged
			},
			sequence: group.sequence,
			dropped,
			sampleIndex: state.sampleIndex,
			...signal === "imu" ? { channelUnits: imuUnits(names) } : {}
		};
		state.sampleIndex += sampleCount;
		return chunk;
	}
	#single(signal, sequence, channels, names, unit, arrivalTime) {
		const state = this.#stateFor(signal);
		const chunk = {
			signal,
			channels,
			channelNames: names,
			timestamps: Float64Array.of(arrivalTime),
			unit,
			sampleRate: 0,
			timing: {
				deviceTicks: sequence,
				arrivalTime,
				uncertaintyUs: 0,
				interpolated: false,
				clockModel: "arrival",
				clockConverged: true
			},
			sequence,
			dropped: 0,
			sampleIndex: state.sampleIndex
		};
		state.sampleIndex += 1;
		return chunk;
	}
};
function imuUnits(names) {
	const out = {};
	for (const name of names) out[name] = name.startsWith("gyro") ? "deg/s" : "g";
	return out;
}
//#endregion
//#region src/quality/signal-quality.ts
/** Half the representable EEG span, in microvolts. */
const EEG_FULL_SCALE_UV = 8192 * ATHENA_SCALING.eegMicrovoltsPerCount;
/** Within this of a rail counts as saturated. */
const RAIL_MARGIN_UV = EEG_FULL_SCALE_UV * .02;
/**
* Plausible band for scalp EEG spread, in microvolts.
*
* Below the floor the channel is not moving at all — a disconnected or shorted
* input. Above the ceiling it is not brain activity but a floating electrode or
* gross artefact. Both bounds come from measurement: worn channels sat at
* 10–63 µV, a headband on a desk at ~600 µV, and a railed auxiliary input at 0.
*/
const EEG_SPREAD_FLOOR_UV = 1;
const EEG_SPREAD_GOOD_UV = 150;
const EEG_SPREAD_USABLE_UV = 400;
const SATURATION_GOOD = .02;
const SATURATION_USABLE = .1;
/**
* Rolling assessment of signal quality.
*
* Feed it chunks; ask for a report whenever you need one. It keeps a bounded
* window per channel so a long session costs the same as a short one.
*/
var SignalQualityMonitor = class {
	#windowSeconds;
	#channels = /* @__PURE__ */ new Map();
	/**
	* Per-chunk counts inside the window. `lossRate` is documented as windowed,
	* like everything else in the report; it was once a lifetime ratio, so one
	* early dropout coloured it for the rest of a session.
	*/
	#loss = [];
	#received = 0;
	#dropped = 0;
	#sampleRate = 0;
	constructor(options = {}) {
		this.#windowSeconds = options.windowSeconds ?? 5;
	}
	reset() {
		this.#channels.clear();
		this.#loss = [];
		this.#received = 0;
		this.#dropped = 0;
	}
	/** Only EEG chunks inform the assessment; other signals have no contact notion. */
	accept(chunk) {
		if (chunk.signal !== "eeg") return;
		this.#sampleRate = chunk.sampleRate;
		this.#dropped += chunk.dropped;
		const capacity = Math.max(1, Math.round(chunk.sampleRate * this.#windowSeconds));
		for (const name of chunk.channelNames) {
			const column = chunk.channels[name];
			if (column === void 0) continue;
			let history = this.#channels.get(name);
			if (history === void 0) {
				history = {
					values: [],
					saturated: 0
				};
				this.#channels.set(name, history);
			}
			for (const value of column) {
				history.values.push(value);
				if (Math.abs(value) >= EEG_FULL_SCALE_UV - RAIL_MARGIN_UV) history.saturated++;
			}
			const excess = history.values.length - capacity;
			if (excess > 0) {
				for (let i = 0; i < excess; i++) {
					const removed = history.values[i];
					if (Math.abs(removed) >= EEG_FULL_SCALE_UV - RAIL_MARGIN_UV) history.saturated--;
				}
				history.values = history.values.slice(excess);
			}
		}
		this.#received += chunk.timestamps.length;
		this.#loss.push({
			received: chunk.timestamps.length,
			dropped: chunk.dropped
		});
		while (this.#loss.length > 1) {
			const front = this.#loss[0];
			if (this.#received + this.#dropped - front.received - front.dropped < capacity) break;
			this.#received -= front.received;
			this.#dropped -= front.dropped;
			this.#loss.shift();
		}
	}
	report() {
		const channels = {};
		for (const [name, history] of this.#channels) {
			const samples = history.values.length;
			const saturationRate = samples === 0 ? 0 : history.saturated / samples;
			const spread = medianAbsoluteDeviation(history.values);
			channels[name] = {
				saturationRate,
				spread,
				samples,
				fit: rate(saturationRate, spread)
			};
		}
		const expected = this.#received + this.#dropped;
		return {
			channels,
			lossRate: expected === 0 ? 0 : this.#dropped / expected,
			headbandOn: this.#inferWorn(channels)
		};
	}
	/**
	* Judges wear from the scalp channels only.
	*
	* Auxiliary inputs are excluded on purpose: with nothing plugged into them
	* they are permanently poor, and letting them vote would report a properly
	* worn headband as unworn.
	*/
	#inferWorn(channels) {
		const scalp = Object.entries(channels).filter(([name]) => ATHENA_CHANNEL_INFO[name]?.scalp === true);
		if (scalp.length === 0) return null;
		if (!scalp.every(([, q]) => q.samples >= Math.max(64, this.#sampleRate / 2))) return null;
		return scalp.filter(([, q]) => q.fit !== 4).length > scalp.length / 2;
	}
};
function rate(saturationRate, spread) {
	if (saturationRate <= SATURATION_GOOD && spread >= EEG_SPREAD_FLOOR_UV && spread <= EEG_SPREAD_GOOD_UV) return 1;
	if (saturationRate <= SATURATION_USABLE && spread >= EEG_SPREAD_FLOOR_UV / 2 && spread <= EEG_SPREAD_USABLE_UV) return 2;
	return 4;
}
/** Median absolute deviation: a spread estimate a few blinks cannot inflate. */
function medianAbsoluteDeviation(values) {
	if (values.length === 0) return 0;
	const sorted = values.toSorted((a, b) => a - b);
	const median = sorted[sorted.length >> 1];
	const deviations = values.map((v) => Math.abs(v - median)).toSorted((a, b) => a - b);
	return deviations[deviations.length >> 1];
}
//#endregion
export { ATHENA_AUX_CHARACTERISTICS, ATHENA_CHANNEL_INFO, ATHENA_CONTROL_CHARACTERISTIC, ATHENA_DATA_CHARACTERISTICS, ATHENA_EEG_CHANNELS_4, ATHENA_EEG_CHANNELS_8, ATHENA_PRESET_PROFILES, ATHENA_SCALING, ATHENA_SENSOR_SPECS, ATHENA_UNIVERSAL_CHARACTERISTIC, BoundedStream, DEVICE_CLOCK_HZ_COARSE, DEVICE_CLOCK_HZ_MILLISECOND, DRL_REF_CHANNELS, IMU_CHANNELS, IMU_CHANNEL_UNITS, LEGACY_ACCELEROMETER_CHARACTERISTIC, LEGACY_ACCELEROMETER_G_PER_COUNT, LEGACY_CONTROL_CHARACTERISTIC, LEGACY_DATA_CHARACTERISTICS, LEGACY_EEG_CHANNEL_NAMES, LEGACY_EEG_CHARACTERISTICS, LEGACY_EEG_MICROVOLTS_PER_COUNT, LEGACY_EEG_SAMPLES_PER_PACKET, LEGACY_EEG_SAMPLE_RATE, LEGACY_EEG_ZERO_COUNT, LEGACY_GYROSCOPE_CHARACTERISTIC, LEGACY_GYROSCOPE_DEG_PER_SECOND_PER_COUNT, LEGACY_IMU_SAMPLES_PER_PACKET, LEGACY_IMU_SAMPLE_RATE, LEGACY_PPG_CHANNEL_NAMES, LEGACY_PPG_CHARACTERISTICS, LEGACY_PPG_SAMPLES_PER_PACKET, LEGACY_PPG_SAMPLE_RATE, LEGACY_TELEMETRY_CHARACTERISTIC, LegacyAssembler, LsbBitReader, MAX_FIELD_WIDTH, MICROSECONDS_PER_MILLISECOND, MICROSECONDS_PER_SECOND, MUSE_COMMANDS, MUSE_ERROR_CODES, MUSE_SERVICE_UUID, MUSE_SERVICE_UUID_16, ManualTimeSource, MuseError, OPTICS_CHANNELS_16, PACKET_HEADER_SIZE, RawArrivalClock, RobustOffsetClock, SUBPACKET_HEADER_SIZE, SampleAssembler, SignalQualityMonitor, UNCONVERGED, WindowedRegressionClock, decodeAthenaNotification, decodeAthenaNotificationStrict, decodeBatteryPercent, decodeCommand, decodeDrlRefCounts, decodeEegCounts, decodeImuCounts, decodeLegacyEeg, decodeLegacyImu, decodeLegacyPpg, decodeLegacyTelemetry, decodeOpticsCounts, detectProtocol, deviceTicksToSeconds, eegChannelLabels, eegCountsToMicrovolts, encodeCommand, estimateDeviceClock, imuCountsToPhysical, isAborted, isAthenaControlCharacteristic, isAthenaDataCharacteristic, isKnownTag, isPresetToken, isRecoverableCode, legacyRole, opticsChannelNames, opticsCountsToUnits, presetCommand, profileForPreset, rawClock, robustClock, sameCharacteristicUuid, scalpChannelIndices, selectPreset, specForTag, systemTimeSource, toCanonicalCharacteristicUuid, windowedClock };

//# sourceMappingURL=index.js.map